import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Plus, Edit, Trash2, Eye, EyeOff, Search, X, Info, Upload, Building2, Globe } from 'lucide-react';
import Swal from 'sweetalert2';

export default function PartnersManagementPage({ onBack }) {
  const [partners, setPartners] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [showPasswords, setShowPasswords] = useState({});
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);

  const [formData, setFormData] = useState({
    libelle_partenaire: '',
    nom: '',
    prenom: '',
    login_partenaire: '',
    password: '',
    email: '',
    tel: '',
    adresse_partenaire: '',
    solde_partenaire: 0,
    marge_partenaire: 10,
    marge_b2b: 5,
    is_b2b: false,
  });

  useEffect(() => { fetchPartners(); }, []);

  const fetchPartners = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/partners`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Erreur lors du chargement');
      const data = await response.json();
      setPartners(data.partners);
    } catch {
      Swal.fire({ icon: 'error', title: 'Erreur', text: 'Impossible de charger les partenaires', confirmButtonColor: '#FF7B47' });
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      libelle_partenaire: '', nom: '', prenom: '', login_partenaire: '',
      password: '', email: '', tel: '', adresse_partenaire: '',
      solde_partenaire: 0, marge_partenaire: 10, marge_b2b: 5, is_b2b: false,
    });
    setLogoFile(null);
    setLogoPreview(null);
    if (logoInputRef.current) logoInputRef.current.value = '';
  };

  const handleCreate = () => {
    setModalMode('create');
    setSelectedPartner(null);
    resetForm();
    setShowModal(true);
  };

  const handleEdit = (partner) => {
    setModalMode('edit');
    setSelectedPartner(partner);
    setFormData({
      libelle_partenaire: partner.libelle_partenaire,
      nom: partner.nom,
      prenom: partner.prenom,
      login_partenaire: partner.login_partenaire,
      password: partner.password,
      email: partner.email,
      tel: partner.tel || '',
      adresse_partenaire: partner.adresse_partenaire || '',
      solde_partenaire: partner.solde_partenaire,
      marge_partenaire: partner.marge_partenaire,
      marge_b2b: partner.marge_b2b,
      is_b2b: !!partner.is_b2b,
    });
    setLogoFile(null);
setLogoPreview(partner.logo_url
  ? `${process.env.REACT_APP_API_URL}/backend${partner.logo_url}`
  : null);
    if (logoInputRef.current) logoInputRef.current.value = '';
    setShowModal(true);
  };

  const handleView = (partner) => { setSelectedPartner(partner); setShowViewModal(true); };

  // ── Logo file picker ─────────────────────────────────────────────
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLogoFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setLogoPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const uploadLogo = async (partnerId) => {
    if (!logoFile) return null;
    setIsUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append('logo', logoFile);
      const token = localStorage.getItem('authToken');
      const resp = await fetch(
      `${process.env.REACT_APP_API_URL}/api/admin/partners/${partnerId}/logo`,
        { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: fd }
      );
      if (!resp.ok) throw new Error('Échec de l\'upload du logo');
      return await resp.json();
    } finally {
      setIsUploadingLogo(false);
    }
  };

  // ── Submit create / edit ─────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('authToken');
      const url    = modalMode === 'create'
        ? `${process.env.REACT_APP_API_URL}/api/admin/partners`
        : `${process.env.REACT_APP_API_URL}/api/admin/partners/${selectedPartner.id_partenaire}`;
      const method = modalMode === 'create' ? 'POST' : 'PUT';

      const response = await fetch(url, {
        method,
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erreur lors de la sauvegarde');
      }
      const result = await response.json();

      // Upload logo if one was selected
      const partnerId = modalMode === 'create' ? result.partnerId : selectedPartner.id_partenaire;
      if (logoFile) {
        await uploadLogo(partnerId);
      }

      Swal.fire({
        icon: 'success', title: 'Succès',
        text: modalMode === 'create' ? 'Partenaire créé avec succès' : 'Partenaire mis à jour',
        confirmButtonColor: '#FF7B47'
      });
      setShowModal(false);
      fetchPartners();
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Erreur', text: error.message, confirmButtonColor: '#FF7B47' });
    }
  };

  const handleDelete = async (partner) => {
    const result = await Swal.fire({
      icon: 'warning', title: 'Confirmer la suppression',
      text: `Êtes-vous sûr de vouloir supprimer ${partner.libelle_partenaire} ?`,
      showCancelButton: true, confirmButtonColor: '#d33', cancelButtonColor: '#3085d6',
      confirmButtonText: 'Oui, supprimer', cancelButtonText: 'Annuler'
    });
    if (!result.isConfirmed) return;
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/partners/${partner.id_partenaire}`,  
        { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (!response.ok) throw new Error('Erreur lors de la suppression');
      Swal.fire({ icon: 'success', title: 'Supprimé', text: 'Partenaire supprimé avec succès', confirmButtonColor: '#FF7B47' });
      fetchPartners();
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Erreur', text: error.message, confirmButtonColor: '#FF7B47' });
    }
  };

  const togglePasswordVisibility = (id) =>
    setShowPasswords(prev => ({ ...prev, [id]: !prev[id] }));

  const filteredPartners = partners.filter(p =>
    p.libelle_partenaire?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.prenom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-6">

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button onClick={onBack} className="flex items-center gap-2 text-[#2B4979] hover:text-[#FF7B47] font-semibold transition">
              <ArrowLeft size={20} /> Retour
            </button>
            <h1 className="text-3xl font-bold text-[#2B4979]">Gestion des Partenaires</h1>
          </div>
          <button onClick={handleCreate} className="flex items-center gap-2 bg-[#FF7B47] hover:bg-[#ff6a30] text-white px-6 py-3 rounded-lg font-semibold transition">
            <Plus size={20} /> Créer Partenaire
          </button>
        </div>

        {/* Search */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text" placeholder="Rechercher par nom, email..."
              value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF7B47] focus:border-transparent"
            />
          </div>
        </div>

        {/* Table */}
        {isLoading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF7B47]"></div>
            <p className="mt-4 text-gray-600">Chargement...</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#2B4979] text-white">
                  <tr>
                    <th className="px-4 py-3 text-left">ID</th>
                    <th className="px-4 py-3 text-left">Logo</th>
                    <th className="px-4 py-3 text-left">Libellé</th>
                    <th className="px-4 py-3 text-left">Nom & Prénom</th>
                    <th className="px-4 py-3 text-left">Email</th>
                    <th className="px-4 py-3 text-right">Solde</th>
                    <th className="px-4 py-3 text-right">Marge B2B %</th>
                    <th className="px-4 py-3 text-center">Type</th>
                    <th className="px-4 py-3 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPartners.map((partner, idx) => (
                    <tr key={partner.id_partenaire}
                        className={`${idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'} hover:bg-blue-50 transition`}>
                      <td className="px-4 py-3 font-semibold text-gray-700">{partner.id_partenaire}</td>

                      {/* Logo cell */}
                      <td className="px-4 py-3">
                        {partner.logo_url ? (
                          <img
                            src={`${process.env.REACT_APP_API_URL}/backend${partner.logo_url}`}   
                            alt={partner.libelle_partenaire}
                            className="h-10 w-auto object-contain rounded"
                          />
                        ) : (
                          <div className="h-10 w-10 bg-gray-100 rounded flex items-center justify-center">
                            <Building2 size={18} className="text-gray-400" />
                          </div>
                        )}
                      </td>

                      <td className="px-4 py-3">
                        <span className="font-semibold text-[#2B4979]">{partner.libelle_partenaire}</span>
                        {partner.is_admin === 1 && (
                          <span className="ml-2 px-2 py-1 bg-red-100 text-red-700 text-xs font-bold rounded">ADMIN</span>
                        )}
                      </td>
                      <td className="px-4 py-3">{partner.prenom} {partner.nom}</td>
                      <td className="px-4 py-3 text-sm">{partner.email}</td>
                      <td className="px-4 py-3 text-right font-semibold text-green-600">
                        {parseFloat(partner.solde_partenaire).toFixed(3)} TND
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-purple-600">
                        {parseFloat(partner.marge_b2b).toFixed(1)}%
                      </td>

                      {/* B2B badge */}
                      <td className="px-4 py-3 text-center">
                        {partner.is_b2b ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 text-xs font-bold rounded-full">
                            <Globe size={11} /> B2B
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-500 text-xs rounded-full">
                            Interne
                          </span>
                        )}
                      </td>

                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-2">
                          <button onClick={() => handleView(partner)}
                            className="p-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg transition" title="Voir détails">
                            <Eye size={16} />
                          </button>
                          <button onClick={() => handleEdit(partner)}
                            className="p-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition" title="Modifier">
                            <Edit size={16} />
                          </button>
                          {!partner.is_admin && (
                            <button onClick={() => handleDelete(partner)}
                              className="p-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition" title="Supprimer">
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredPartners.length === 0 && (
                <div className="text-center py-12 text-gray-500">Aucun partenaire trouvé</div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── View Modal ── */}
      {showViewModal && selectedPartner && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-purple-50">
              <div className="flex items-center gap-3">
                <Info className="text-purple-600" size={28} />
                <h2 className="text-2xl font-bold text-[#2B4979]">Détails du Partenaire</h2>
              </div>
              <button onClick={() => setShowViewModal(false)} className="text-gray-400 hover:text-gray-600 transition">
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="flex items-center gap-4 flex-wrap">
                <div className="px-4 py-2 bg-gray-100 rounded-lg">
                  <span className="text-sm text-gray-600">ID:</span>
                  <span className="ml-2 text-lg font-bold text-[#2B4979]">{selectedPartner.id_partenaire}</span>
                </div>
                {selectedPartner.is_admin === 1 && (
                  <span className="px-4 py-2 bg-red-100 text-red-700 text-sm font-bold rounded-lg">ADMINISTRATEUR</span>
                )}
                {selectedPartner.is_b2b ? (
                  <span className="inline-flex items-center gap-1 px-4 py-2 bg-blue-100 text-blue-700 text-sm font-bold rounded-lg">
                    <Globe size={14} /> Partenaire B2B externe
                  </span>
                ) : (
                  <span className="px-4 py-2 bg-gray-100 text-gray-500 text-sm rounded-lg">Partenaire interne</span>
                )}
              </div>

              {/* Logo display */}
              {selectedPartner.logo_url && (
                <div>
                  <label className="text-sm font-semibold text-gray-600">Logo</label>
                  <div className="mt-2 p-4 bg-gray-50 rounded-lg inline-block">
                    <img
                      src={`${process.env.REACT_APP_API_URL}/backend${selectedPartner.logo_url}`}  
                      alt={selectedPartner.libelle_partenaire}
                      className="max-h-20 object-contain"
                    />
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><label className="text-sm font-semibold text-gray-600">Libellé</label>
                  <p className="text-lg font-semibold text-[#2B4979]">{selectedPartner.libelle_partenaire}</p></div>
                <div><label className="text-sm font-semibold text-gray-600">Nom Complet</label>
                  <p className="text-lg">{selectedPartner.prenom} {selectedPartner.nom}</p></div>
                <div><label className="text-sm font-semibold text-gray-600">Email</label>
                  <p className="text-lg">{selectedPartner.email}</p></div>
                <div><label className="text-sm font-semibold text-gray-600">Téléphone</label>
                  <p className="text-lg">{selectedPartner.tel || '-'}</p></div>
              </div>

              {selectedPartner.adresse_partenaire && (
                <div><label className="text-sm font-semibold text-gray-600">Adresse</label>
                  <p className="text-lg p-3 bg-gray-50 rounded-lg">{selectedPartner.adresse_partenaire}</p></div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-blue-50 rounded-lg">
                <div><label className="text-sm font-semibold text-blue-700">Login</label>
                  <p className="text-lg font-mono">{selectedPartner.login_partenaire}</p></div>
                <div><label className="text-sm font-semibold text-blue-700">Mot de passe</label>
                  <div className="flex items-center gap-2">
                    <p className="text-lg font-mono">
                      {showPasswords[selectedPartner.id_partenaire] ? selectedPartner.password : '••••••••'}
                    </p>
                    <button onClick={() => togglePasswordVisibility(selectedPartner.id_partenaire)}
                      className="text-blue-600 hover:text-blue-800 transition">
                      {showPasswords[selectedPartner.id_partenaire] ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 bg-green-50 rounded-lg">
                  <label className="text-sm font-semibold text-green-700">Solde</label>
                  <p className="text-2xl font-bold text-green-600">{parseFloat(selectedPartner.solde_partenaire).toFixed(3)} TND</p>
                </div>
                <div className="p-4 bg-orange-50 rounded-lg">
                  <label className="text-sm font-semibold text-orange-700">Marge Partenaire</label>
                  <p className="text-2xl font-bold text-orange-600">{parseFloat(selectedPartner.marge_partenaire).toFixed(1)}%</p>
                </div>
                <div className="p-4 bg-purple-50 rounded-lg">
                  <label className="text-sm font-semibold text-purple-700">Marge B2B</label>
                  <p className="text-2xl font-bold text-purple-600">{parseFloat(selectedPartner.marge_b2b).toFixed(1)}%</p>
                </div>
              </div>

              {selectedPartner.created_at && (
                <div className="text-sm text-gray-500 text-center pt-4 border-t">
                  Compte créé le {new Date(selectedPartner.created_at).toLocaleDateString('fr-FR')}
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
              <button onClick={() => setShowViewModal(false)}
                className="px-6 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-semibold transition">Fermer</button>
              <button onClick={() => { setShowViewModal(false); handleEdit(selectedPartner); }}
                className="px-6 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-semibold transition flex items-center gap-2">
                <Edit size={18} /> Modifier
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Create / Edit Modal ── */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-[#2B4979]">
                {modalMode === 'create' ? 'Créer un Partenaire' : 'Modifier le Partenaire'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600 transition">
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Libellé *</label>
                  <input type="text" required value={formData.libelle_partenaire}
                    onChange={e => setFormData({...formData, libelle_partenaire: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF7B47] focus:border-transparent" />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Nom *</label>
                  <input type="text" required value={formData.nom}
                    onChange={e => setFormData({...formData, nom: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF7B47] focus:border-transparent" />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Prénom *</label>
                  <input type="text" required value={formData.prenom}
                    onChange={e => setFormData({...formData, prenom: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF7B47] focus:border-transparent" />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Login *</label>
                  <input type="text" required value={formData.login_partenaire}
                    onChange={e => setFormData({...formData, login_partenaire: e.target.value})}
                    disabled={modalMode === 'edit'}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF7B47] focus:border-transparent disabled:bg-gray-100" />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Mot de passe *</label>
                  <input type="text" required={modalMode === 'create'} value={formData.password}
                    onChange={e => setFormData({...formData, password: e.target.value})}
                    placeholder={modalMode === 'edit' ? 'Laisser vide pour ne pas changer' : ''}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF7B47] focus:border-transparent" />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Email *</label>
                  <input type="email" required value={formData.email}
                    onChange={e => setFormData({...formData, email: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF7B47] focus:border-transparent" />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Téléphone</label>
                  <input type="text" value={formData.tel}
                    onChange={e => setFormData({...formData, tel: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF7B47] focus:border-transparent" />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Solde (TND)</label>
                  <input type="number" step="0.001" value={formData.solde_partenaire}
                    onChange={e => setFormData({...formData, solde_partenaire: parseFloat(e.target.value)})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF7B47] focus:border-transparent" />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Marge B2B (%)</label>
                  <input type="number" step="0.1" min="0" max="100" value={formData.marge_b2b}
                    onChange={e => setFormData({...formData, marge_b2b: parseFloat(e.target.value)})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF7B47] focus:border-transparent"
                    placeholder="5.0" />
                  <p className="text-xs text-gray-500 mt-1">Marge Partenaire sera définie à 10% par défaut</p>
                </div>

                {/* ── is_b2b toggle ── */}
                <div className="flex items-center">
                  <div className="w-full">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Type de partenaire</label>
                    <button
                      type="button"
                      onClick={() => setFormData(f => ({ ...f, is_b2b: !f.is_b2b }))}
                      className={`w-full flex items-center justify-between px-4 py-3 rounded-lg border-2 transition ${
                        formData.is_b2b
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-200 bg-gray-50 text-gray-600'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <Globe size={18} />
                        <span className="font-semibold">
                          {formData.is_b2b ? 'B2B Externe (voucher personnalisé)' : 'Interne (voucher standard)'}
                        </span>
                      </div>
                      {/* Toggle pill */}
                      <div className={`relative w-12 h-6 rounded-full transition-colors ${formData.is_b2b ? 'bg-blue-500' : 'bg-gray-300'}`}>
                        <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${formData.is_b2b ? 'translate-x-7' : 'translate-x-1'}`} />
                      </div>
                    </button>
                    <p className="text-xs text-gray-500 mt-1">
                      {formData.is_b2b
                        ? 'Le voucher MyGO sera personnalisé avec le logo et les infos de ce partenaire.'
                        : 'Le voucher MyGO original sera utilisé sans modification.'}
                    </p>
                  </div>
                </div>

                {/* ── Logo upload ── */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Logo du partenaire
                    {formData.is_b2b && <span className="ml-1 text-blue-600">(apparaîtra sur le voucher)</span>}
                  </label>

                  <div className="flex items-start gap-4">
                    {/* Preview */}
                    <div className="w-24 h-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50 overflow-hidden flex-shrink-0">
                      {logoPreview ? (
                        <img src={logoPreview} alt="Logo preview" className="w-full h-full object-contain" />
                      ) : (
                        <Building2 size={32} className="text-gray-300" />
                      )}
                    </div>

                    <div className="flex-1">
                      <input
                        ref={logoInputRef}
                        type="file"
                        accept=".jpg,.jpeg,.png,.webp"
                        onChange={handleLogoChange}
                        className="hidden"
                        id="logo-upload"
                      />
                      <label
                        htmlFor="logo-upload"
                        className="flex items-center gap-2 cursor-pointer px-4 py-2 border-2 border-[#FF7B47] text-[#FF7B47] rounded-lg hover:bg-orange-50 transition font-semibold text-sm w-fit"
                      >
                        <Upload size={16} />
                        {logoPreview ? 'Changer le logo' : 'Choisir un logo'}
                      </label>
                      <p className="text-xs text-gray-500 mt-2">JPG, PNG ou WebP · Max 2 MB</p>
                      {logoFile && (
                        <p className="text-xs text-green-600 mt-1 font-semibold">✓ {logoFile.name}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Address */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Adresse</label>
                  <textarea rows="3" value={formData.adresse_partenaire}
                    onChange={e => setFormData({...formData, adresse_partenaire: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF7B47] focus:border-transparent resize-none"
                    placeholder="Entrez l'adresse complète..." />
                </div>

              </div>

              <div className="flex items-center justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
                <button type="button" onClick={() => setShowModal(false)}
                  className="px-6 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-semibold transition">
                  Annuler
                </button>
                <button type="submit" disabled={isUploadingLogo}
                  className="px-6 py-2 bg-[#FF7B47] hover:bg-[#ff6a30] disabled:opacity-60 text-white rounded-lg font-semibold transition flex items-center gap-2">
                  {isUploadingLogo && (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  )}
                  {modalMode === 'create' ? 'Créer' : 'Mettre à jour'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}